import { startTransition, useEffect, useMemo, useState } from "react";
import { catalogFactor, EMPTY_OVERRIDES, type CatalogOverrides } from "@glastotaal/core";
import { getDb, loadFirestoreModule } from "@/lib/firebase/db";
import { firebaseIdle } from "@/lib/firebase/app";
import { withZeroPricedDisabled } from "@/lib/catalog";
import { bewaarStartwaarde, leesStartwaarde, zelfdeWaarde } from "@/lib/start-cache";

/**
 * Live catalogus-overrides uit Firestore `settings/catalog` (admin-bewerkbaar, publiek
 * leesbaar). Eén gecachte read per sessie; de zware catalogus blijft statisch, dus de
 * site blijft snel. Ontbreekt het doc (of mislukt de read, bv. App Check op localhost),
 * dan vallen we terug op géén overrides (de statische prijzen).
 */
// Begint met de waarde die deze browser de vorige keer las (zie start-cache.ts): anders stond
// bij elk bezoek eerst de kale catalogusprijs in beeld en sprong die een seconde later omhoog.
let cached: CatalogOverrides | null = leesStartwaarde<CatalogOverrides>("catalog");
/** Is `cached` al de live waarde van dít bezoek? Zo niet, dan moet Firestore nog gelezen worden. */
let live = false;
const subscribers = new Set<(o: CatalogOverrides) => void>();
// Meerdere secties (SiteLayout, SearchBar×2, FeaturedDeals, RecentlyViewed, GlassCategories, ...)
// monteren deze hook tegelijk op de homepage. Zonder deze in-flight-gate zag elke instantie
// `cached === null` en startte elk zíjn EIGEN `readDoc()` — tot 8 identieke Firestore-requests
// i.p.v. 1.
let pending: Promise<CatalogOverrides | null> | null = null;

function coerce(raw: Record<string, unknown> | undefined): CatalogOverrides {
  if (!raw) return EMPTY_OVERRIDES;
  const out: CatalogOverrides = {};
  if (typeof raw.priceFactor === "number") out.priceFactor = raw.priceFactor;
  if (raw.priceFactorPerCategorie && typeof raw.priceFactorPerCategorie === "object") {
    out.priceFactorPerCategorie = raw.priceFactorPerCategorie as Record<string, number>;
  }
  if (raw.priceFactorPerSlug && typeof raw.priceFactorPerSlug === "object") {
    out.priceFactorPerSlug = raw.priceFactorPerSlug as Record<string, number>;
  }
  if (raw.priceOverrideCents && typeof raw.priceOverrideCents === "object") {
    out.priceOverrideCents = raw.priceOverrideCents as Record<string, number>;
  }
  if (raw.nameOverride && typeof raw.nameOverride === "object") {
    out.nameOverride = raw.nameOverride as Record<string, string>;
  }
  if (raw.articleNumberOverride && typeof raw.articleNumberOverride === "object") {
    out.articleNumberOverride = raw.articleNumberOverride as Record<string, string>;
  }
  if (Array.isArray(raw.disabled)) out.disabled = raw.disabled.map(String);
  if (raw.leadTimePerSoort && typeof raw.leadTimePerSoort === "object") {
    out.leadTimePerSoort = raw.leadTimePerSoort as Record<string, string>;
  }
  if (raw.hideZeroPriced === true) out.hideZeroPriced = true;
  // Deze twee stonden er niet in en werden dus stilzwijgend weggegooid: de admin sloeg ze op,
  // maar de site las ze nooit. Elk nieuw override-veld hoort óók hier. (29-07-2026.)
  if (Array.isArray(raw.cutToLength)) out.cutToLength = raw.cutToLength.map(String);
  if (raw.profielVerkoopPerSlug && typeof raw.profielVerkoopPerSlug === "object") {
    out.profielVerkoopPerSlug = raw.profielVerkoopPerSlug as CatalogOverrides["profielVerkoopPerSlug"];
  }
  if (raw.systeemPerSlug && typeof raw.systeemPerSlug === "object") {
    out.systeemPerSlug = raw.systeemPerSlug as Record<string, string>;
  }
  if (Array.isArray(raw.eigenTransport)) out.eigenTransport = raw.eigenTransport.map(String);
  return out;
}

function readDoc(): Promise<CatalogOverrides | null> {
  return Promise.all([loadFirestoreModule(), getDb()])
    .then(([{ doc, getDoc }, db]) => getDoc(doc(db, "settings", "catalog")))
    .then((s) => (s.exists() ? coerce(s.data() as Record<string, unknown>) : EMPTY_OVERRIDES))
    .catch(() => null);
}

function loadOnce(): Promise<CatalogOverrides | null> {
  if (live && cached != null) return Promise.resolve(cached);
  if (!pending) {
    pending = firebaseIdle().then(readDoc).then((c) => {
      pending = null;
      if (c != null) {
        live = true;
        bewaarStartwaarde("catalog", c);
        // Zelfde marges als waarmee we begonnen? Dan niemand opnieuw laten tekenen.
        if (zelfdeWaarde(c, cached)) return cached;
        cached = c;
        // Op de homepage wachten tegelijk tientallen componenten op dit resultaat (SiteLayout,
        // SearchBar×2, FeaturedDeals, RecentlyViewed, GlassCategories, ...) — zonder
        // startTransition zet React al hun re-renders in ÉÉN synchrone, niet-onderbreekbare
        // taak, precies de "lange taak" die TBT straft. startTransition markeert dit als
        // laag-prioriteit: de browser mag ertussendoor schilderen/interactie verwerken.
        startTransition(() => { subscribers.forEach((fn) => fn(c)); });
      }
      return c;
    });
  }
  return pending;
}

/**
 * Live overrides; lege overrides tot de Firestore-read klaar is. De €0-verberg-optie wordt hier
 * toegepast (afgeleide `disabled`-set), zodat álle bestaande `isDisabled`-filters die items mee
 * verbergen zonder aanpassing. De admin gebruikt de RAW overrides (fetchCatalogOverrides).
 */
/**
 * Of de live overrides al binnen zijn. De statische catalogusprijs is de INKOOP-kant zonder de
 * admin-marge; tonen we die vast, dan springt het bedrag een seconde later omhoog en betaalt de
 * klant iets anders dan hij eerst zag. Met dit signaal kan een pagina wachten met het tonen van
 * een prijs tot hij klopt.
 */
export function useOverridesReady(): boolean {
  const [ready, setReady] = useState(cached != null);
  useEffect(() => {
    if (cached != null) {
      setReady(true);
      if (!live) void loadOnce();
      return;
    }
    const onChange = (): void => setReady(true);
    subscribers.add(onChange);
    void loadOnce().then((c) => {
      if (c == null) setReady(true); // Firestore onbereikbaar → liever de catalogusprijs dan een leeg vak
    });
    return () => { subscribers.delete(onChange); };
  }, []);
  return ready;
}

/**
 * Dezelfde live overrides, maar buiten React — voor prijshelpers die geen hook kunnen aanroepen
 * (bv. de "vanaf"-prijs van een douche-opstelling). Leeg tot de read klaar is; gebruik
 * `useOverridesReady()` als een bedrag pas getoond mag worden zodra de marge bekend is.
 */
export function cachedCatalogOverrides(): CatalogOverrides {
  return cached ?? EMPTY_OVERRIDES;
}

export function useCatalogOverrides(): CatalogOverrides {
  const [v, setV] = useState<CatalogOverrides>(cached ?? EMPTY_OVERRIDES);
  useEffect(() => {
    // Altijd abonneren: ook met een startwaarde kan de live read nog een gewijzigde marge brengen.
    const onChange = (o: CatalogOverrides): void => setV(o);
    subscribers.add(onChange);
    if (cached != null) setV(cached);
    // Passieve read → wacht op idle zodat Firebase niet de eerste render belast.
    if (!live) void loadOnce();
    return () => { subscribers.delete(onChange); };
  }, []);
  return useMemo(() => withZeroPricedDisabled(v), [v]);
}

/** Ingestelde marge/opslag (%) op een product: per-slug wint van per-categorie/globaal. */
export function marginPctFor(ov: CatalogOverrides, slug: string, cats: string[]): number {
  const factor = ov.priceFactorPerSlug?.[slug] ?? catalogFactor(ov, cats);
  return Math.round((factor - 1) * 100);
}

/** Eenmalige fetch voor de admin-form; live waarden of lege fallback. */
export async function fetchCatalogOverrides(): Promise<CatalogOverrides> {
  const c = await readDoc();
  if (c != null) {
    cached = c;
    live = true;
    bewaarStartwaarde("catalog", c);
  }
  return c ?? EMPTY_OVERRIDES;
}

/** Cache + alle abonnees bijwerken na een admin-save (zonder herladen). */
export function setCachedCatalogOverrides(next: CatalogOverrides): void {
  cached = next;
  live = true;
  bewaarStartwaarde("catalog", next);
  subscribers.forEach((fn) => fn(next));
}
